import React, { useState, useEffect, useRef } from 'react';
import { Modal, Container, Row, Col, Card } from 'react-bootstrap';

const Experience = () => {
  const projects = [
    {
      id: 1,
      title: "API Management with Azure API Management",
      description: "Architected a centralized API gateway solution using Azure API Management (APIM) to streamline service integration and enforce policies across multiple backend services.",
      technologies: ["Azure API Management", "Service Bus", "Function Apps", "Application Insights"],
      achievements: [
        "Implemented rate limiting and throttling policies to protect backend services",
        "Set up subscription-based access control with automatic key rotation",
        "Configured request/response transformations to standardize API contracts",
        "Integrated with Application Insights for real-time monitoring and diagnostics"
      ],
      image: "/images/Experience/EXP-APIM.png"
    },
    {
      id: 2,
      title: "4me Integration with Azure using IaC & Hub-Spoke Architecture",
      description: "Architected enterprise integration between 4me Service Management Platform and Azure cloud services using Infrastructure as Code (Terraform) and Azure DevOps, implementing a secure Hub-Spoke network topology.",
      technologies: ["Azure DevOps", "Terraform", "Azure Kubernetes Service", "Azure Service Bus", "Azure Container Registry", "Azure Logic Apps", "Azure Bastion", "Entra ID"],
      achievements: [
        "Implemented IaC using Terraform with Azure DevOps pipelines for automated infrastructure provisioning",
        "Designed Hub-Spoke VNet architecture with Azure Bastion for secure VM access",
        "Integrated 4me platform with Azure App Service and Service Bus for event-driven workflows",
        "Configured Azure Logic Apps for automated email notifications to end users",
        "Set up Azure Container Registry with AKS for containerized workloads",
        "Managed Entra ID users and Azure Playground Subscriptions for developer environments"
      ],
      image: "/images/Experience/EXP-AKS.png"
    },
    {
      id: 3,
      title: "CI/CD Pipeline with Self-Hosted Agents & Monitoring Stack",
      description: "Designed and implemented comprehensive CI/CD pipelines using Azure DevOps with self-hosted agents for multi-platform application builds (iOS, Android, Web Portal), integrated with monitoring solutions including Datadog and self-hosted Grafana.",
      technologies: ["Azure DevOps", "YAML Pipelines", "Azure Artifacts", "Self-Hosted Agents", "Datadog", "Grafana", "ClickHouse", "SQL Server on Azure VM", "NGINX"],
      achievements: [
        "Built YAML pipelines for multi-platform builds targeting iOS, Android, and Web Portal applications",
        "Deployed self-hosted build and release agents on Azure VMs for enhanced control and performance",
        "Configured Azure Artifacts for centralized package management and artifact storage",
        "Set up self-hosted Datadog instance with dashboards, monitors, alerts, and case management",
        "Implemented Grafana monitoring stack with custom dashboards and alerting on Azure VMs",
        "Integrated ClickHouse for high-performance application log storage and analytics",
        "Deployed SQL Server on Azure VM for application database with full administrative control"
      ],
      image: "/images/Experience/EXP-CICD.png"
    }
  ];

  const [selectedProject, setSelectedProject] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [drawingMode, setDrawingMode] = useState(false);
  const [highlightMode, setHighlightMode] = useState(true); // Spotlight mode as default
  const [drawingColor, setDrawingColor] = useState('#FF0000');
  const [highlightColor, setHighlightColor] = useState('#FFFF00');
  const [highlightSize, setHighlightSize] = useState(60);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const highlightCanvasRef = useRef(null);

  const handleProjectClick = (project) => {
    setSelectedProject(project);
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setSelectedProject(null);
  };

  const isMobile = () => window.innerWidth <= 768;

  const handleImageClick = () => {
    setShowModal(false);
    setShowImageViewer(true);
    // Disable edit modes on mobile, enable highlight on desktop
    if (isMobile()) {
      setHighlightMode(false);
      setDrawingMode(false);
    } else {
      setHighlightMode(true);
      setDrawingMode(false);
    }
  };

  const handleCloseImageViewer = () => {
    setShowImageViewer(false);
    setDrawingMode(false);
    setHighlightMode(false);
    setShowModal(true);
  };

  const toggleDrawingMode = () => {
    setDrawingMode(!drawingMode);
    if (!drawingMode) {
      setHighlightMode(false); // Disable highlight when enabling draw
    }
  };

  const toggleHighlightMode = () => {
    setHighlightMode(!highlightMode);
    if (!highlightMode) {
      setDrawingMode(false); // Disable draw when enabling highlight
    }
  };

  const clearDrawings = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!showModal || !selectedProject) return;

      const currentIndex = projects.findIndex(p => p.id === selectedProject.id);

      if (e.key === 'ArrowLeft' && currentIndex > 0) {
        setSelectedProject(projects[currentIndex - 1]);
      } else if (e.key === 'ArrowRight' && currentIndex < projects.length - 1) {
        setSelectedProject(projects[currentIndex + 1]);
      } else if (e.key === 'Escape') {
        if (showImageViewer) {
          handleCloseImageViewer();
        } else {
          handleClose();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showModal, selectedProject, projects, showImageViewer]);

  // Drawing functionality
  const [isDrawing, setIsDrawing] = useState(false);

  const startDrawing = (e) => {
    if (!drawingMode) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const ctx = canvas.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing || !drawingMode) return;

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = drawingColor;
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  // Highlight/Spotlight functionality
  const handleMouseMove = (e) => {
    if (!highlightMode) return;

    const canvas = highlightCanvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    setMousePos({ x, y });

    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Create radial gradient for spotlight effect
    const gradient = ctx.createRadialGradient(x, y, 0, x, y, highlightSize);
    gradient.addColorStop(0, highlightColor + 'AA'); // 67% opacity at center
    gradient.addColorStop(0.5, highlightColor + '44'); // 27% opacity
    gradient.addColorStop(1, 'transparent');

    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  // Navigation functions
  const goToPrevious = () => {
    const currentIndex = projects.findIndex(p => p.id === selectedProject.id);
    if (currentIndex > 0) {
      setSelectedProject(projects[currentIndex - 1]);
    }
  };

  const goToNext = () => {
    const currentIndex = projects.findIndex(p => p.id === selectedProject.id);
    if (currentIndex < projects.length - 1) {
      setSelectedProject(projects[currentIndex + 1]);
    }
  };

  const currentIndex = selectedProject ? projects.findIndex(p => p.id === selectedProject.id) : -1;

  return (
    <>
      <Container className="py-5">
        <h2 className="text-center mb-5" style={{ color: '#0078D4', fontWeight: 'bold' }}>
          Professional Experience
        </h2>
        <Row>
          {projects.map((project) => (
            <Col key={project.id} md={4} className="mb-4">
              <Card 
                className="h-100 project-card"
                style={{ 
                  cursor: 'pointer', 
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  border: '2px solid #0078D4',
                  boxShadow: '0 4px 8px rgba(0, 120, 212, 0.1)'
                }}
                onClick={() => handleProjectClick(project)}
              >
                <div style={{ 
                  height: '200px', 
                  overflow: 'hidden', 
                  backgroundColor: '#f8f9fa',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '10px'
                }}>
                  <Card.Img 
                    variant="top" 
                    src={project.image} 
                    alt={project.title}
                    style={{ 
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain'
                    }}
                  />
                </div>
                <Card.Body>
                  <Card.Title style={{ color: '#0078D4', fontSize: '1.1rem', fontWeight: 'bold' }}>
                    {project.title}
                  </Card.Title>
                  <Card.Text style={{ fontSize: '0.9rem', color: '#555' }}>
                    {project.description.substring(0, 100)}...
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      </Container>

      {/* Project Details Modal */}
      <Modal 
        show={showModal && !showImageViewer} 
        onHide={handleClose} 
        size="lg"
        centered
      >
        <Modal.Header closeButton style={{ borderBottom: '2px solid #0078D4' }}>
          <Modal.Title style={{ color: '#0078D4' }}>
            {selectedProject?.title}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedProject && (
            <>
              <div style={{ marginBottom: '20px', cursor: 'pointer' }} onClick={handleImageClick}>
                <img 
                  src={selectedProject.image} 
                  alt={selectedProject.title}
                  style={{ 
                    width: '100%', 
                    borderRadius: '8px',
                    border: '2px solid #0078D4',
                    transition: 'transform 0.3s',
                    padding: '10px',
                    backgroundColor: '#f8f9fa'
                  }}
                  onMouseEnter={(e) => e.target.style.transform = 'scale(1.02)'}
                  onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                />
                <p style={{ textAlign: 'center', marginTop: '10px', color: '#0078D4', fontWeight: 'bold' }}>
                  Click to view full-size interactive diagram
                </p>
              </div>

              <h5 style={{ color: '#0078D4', marginTop: '20px' }}>Description</h5>
              <p>{selectedProject.description}</p>

              <h5 style={{ color: '#0078D4', marginTop: '20px' }}>Key Achievements</h5>
              <ul>
                {selectedProject.achievements.map((achievement, index) => (
                  <li key={index} style={{ marginBottom: '8px' }}>{achievement}</li>
                ))}
              </ul>

              <h5 style={{ color: '#0078D4', marginTop: '20px' }}>Technologies Used</h5>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                {selectedProject.technologies.map((tech, index) => (
                  <span 
                    key={index}
                    style={{
                      backgroundColor: '#0078D4',
                      color: 'white',
                      padding: '5px 15px',
                      borderRadius: '20px',
                      fontSize: '0.9rem'
                    }}
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </>
          )}
        </Modal.Body>

        {/* Navigation Arrows */}
        {currentIndex > 0 && (
          <button
            className="modal-nav-btn modal-nav-prev"
            onClick={goToPrevious}
            aria-label="Previous project"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
          </button>
        )}
        
        {currentIndex < projects.length - 1 && (
          <button
            className="modal-nav-btn modal-nav-next"
            onClick={goToNext}
            aria-label="Next project"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </button>
        )}
      </Modal>

      {/* Full-Screen Interactive Image Viewer */}
      {showImageViewer && selectedProject && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0, 0, 0, 0.95)',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {/* Toolbar */}
          <div style={{
            position: 'absolute',
            top: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: '10px',
            zIndex: 10001,
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            padding: '15px',
            borderRadius: '10px',
            backdropFilter: 'blur(10px)'
          }}>
            {/* Highlight Mode Button */}
            <button
              onClick={toggleHighlightMode}
              style={{
                padding: '10px 20px',
                backgroundColor: highlightMode ? '#FF8C00' : 'white',
                color: highlightMode ? 'white' : '#333',
                border: '2px solid #FF8C00',
                borderRadius: '5px',
                cursor: 'pointer',
                fontWeight: 'bold',
                fontSize: '16px'
              }}
            >
              💡 Highlight
            </button>

            {/* Highlight Color Palette */}
            {highlightMode && (
              <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                {[
                  { color: '#FFFF00', name: 'Yellow' },
                  { color: '#FF8C00', name: 'Orange' },
                  { color: '#00FF00', name: 'Green' },
                  { color: '#FF00FF', name: 'Magenta' },
                  { color: '#00FFFF', name: 'Cyan' }
                ].map(({ color, name }) => (
                  <button
                    key={color}
                    onClick={() => setHighlightColor(color)}
                    style={{
                      width: '30px',
                      height: '30px',
                      backgroundColor: color,
                      border: highlightColor === color ? '3px solid white' : '2px solid #666',
                      borderRadius: '50%',
                      cursor: 'pointer',
                      boxShadow: highlightColor === color ? '0 0 10px rgba(255,255,255,0.8)' : 'none'
                    }}
                    title={name}
                  />
                ))}
                <div style={{ marginLeft: '10px', color: 'white', display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <label style={{ fontSize: '14px' }}>Size:</label>
                  <input
                    type="range"
                    min="30"
                    max="120"
                    value={highlightSize}
                    onChange={(e) => setHighlightSize(Number(e.target.value))}
                    style={{ width: '100px' }}
                  />
                  <span style={{ fontSize: '14px', minWidth: '35px' }}>{highlightSize}px</span>
                </div>
              </div>
            )}

            {/* Draw Mode Button */}
            <button
              onClick={toggleDrawingMode}
              style={{
                padding: '10px 20px',
                backgroundColor: drawingMode ? '#0078D4' : 'white',
                color: drawingMode ? 'white' : '#333',
                border: '2px solid #0078D4',
                borderRadius: '5px',
                cursor: 'pointer',
                fontWeight: 'bold',
                fontSize: '16px'
              }}
            >
              ✏️ Draw
            </button>

            {/* Drawing Color Palette */}
            {drawingMode && (
              <div style={{ display: 'flex', gap: '5px' }}>
                {['#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#FF00FF'].map(color => (
                  <button
                    key={color}
                    onClick={() => setDrawingColor(color)}
                    style={{
                      width: '30px',
                      height: '30px',
                      backgroundColor: color,
                      border: drawingColor === color ? '3px solid white' : '2px solid #666',
                      borderRadius: '50%',
                      cursor: 'pointer',
                      boxShadow: drawingColor === color ? '0 0 10px rgba(255,255,255,0.8)' : 'none'
                    }}
                  />
                ))}
              </div>
            )}

            {/* Clear Button */}
            {drawingMode && (
              <button
                onClick={clearDrawings}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '16px'
                }}
              >
                🗑️ Clear
              </button>
            )}

            {/* Close Button */}
            <button
              onClick={handleCloseImageViewer}
              style={{
                padding: '10px 20px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
                fontWeight: 'bold',
                fontSize: '16px'
              }}
            >
              ✖ Close
            </button>
          </div>

          {/* Image Container */}
          <div style={{
            position: 'relative',
            maxWidth: '90vw',
            maxHeight: '85vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <img
              ref={imageRef}
              src={selectedProject.image}
              alt={selectedProject.title}
              style={{
                maxWidth: '100%',
                maxHeight: '85vh',
                objectFit: 'contain'
              }}
              onLoad={(e) => {
                const canvas = canvasRef.current;
                const highlightCanvas = highlightCanvasRef.current;
                if (canvas && highlightCanvas) {
                  canvas.width = e.target.offsetWidth;
                  canvas.height = e.target.offsetHeight;
                  highlightCanvas.width = e.target.offsetWidth;
                  highlightCanvas.height = e.target.offsetHeight;
                }
              }}
            />
            {/* Drawing Canvas */}
            <canvas
              ref={canvasRef}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                cursor: drawingMode ? 'crosshair' : 'default',
                pointerEvents: drawingMode ? 'auto' : 'none'
              }}
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
            />
            {/* Highlight Canvas */}
            <canvas
              ref={highlightCanvasRef}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                cursor: highlightMode ? 'none' : 'default',
                pointerEvents: highlightMode ? 'auto' : 'none'
              }}
              onMouseMove={handleMouseMove}
            />
          </div>

          {/* Bottom Info */}
          <div style={{
            position: 'absolute',
            bottom: '20px',
            color: 'white',
            fontSize: '14px',
            textAlign: 'center'
          }}>
            <p style={{ margin: '5px 0' }}>
              {highlightMode ? '💡 Highlight Mode Active' : ''}
              {drawingMode ? '✏️ Drawing Mode Active' : ''}
              {!highlightMode && !drawingMode ? 'Select a mode from the toolbar above' : ''}
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default Experience;
